/**
 * Thin wrapper around `window.Telegram.WebApp` so we can stub it out in dev / tests.
 * The real SDK is loaded via the `<script src="…/telegram-web-app.js">` tag in index.html.
 */

interface ThemeParams {
  bg_color?: string;
  text_color?: string;
  hint_color?: string;
  link_color?: string;
  button_color?: string;
  button_text_color?: string;
  secondary_bg_color?: string;
  header_bg_color?: string;
  accent_text_color?: string;
}

interface TelegramWebApp {
  initData: string;
  initDataUnsafe: { user?: { id: number; first_name: string; username?: string; photo_url?: string } };
  version: string;
  colorScheme: 'light' | 'dark';
  themeParams: ThemeParams;
  ready: () => void;
  expand: () => void;
  close: () => void;
  setHeaderColor: (color: string) => void;
  setBackgroundColor: (color: string) => void;
  openInvoice: (
    url: string,
    callback?: (status: 'paid' | 'cancelled' | 'failed' | 'pending') => void,
  ) => void;
  HapticFeedback?: {
    impactOccurred: (style: 'light' | 'medium' | 'heavy' | 'rigid' | 'soft') => void;
    notificationOccurred: (type: 'error' | 'success' | 'warning') => void;
    selectionChanged: () => void;
  };
}

declare global {
  interface Window {
    Telegram?: { WebApp: TelegramWebApp };
  }
}

export function getTelegram(): TelegramWebApp | null {
  return window.Telegram?.WebApp ?? null;
}

export function isInsideTelegram(): boolean {
  const tg = getTelegram();
  return !!tg && tg.initData.length > 0;
}

export function bootstrapTelegram(): void {
  const tg = getTelegram();
  if (!tg) return;
  tg.ready();
  tg.expand();
  tg.setBackgroundColor('#102A2A');
  tg.setHeaderColor('#102A2A');
}

export function haptic(style: 'light' | 'medium' | 'heavy' = 'light'): void {
  getTelegram()?.HapticFeedback?.impactOccurred(style);
}

export function hapticSuccess(): void {
  getTelegram()?.HapticFeedback?.notificationOccurred('success');
}

export function hapticError(): void {
  getTelegram()?.HapticFeedback?.notificationOccurred('error');
}

/**
 * Returns initData when running inside Telegram. In dev (browser) returns a stub
 * that the API will reject — caller is expected to short-circuit to a "dev mode"
 * sign-in flow.
 */
export function getInitData(): string {
  return getTelegram()?.initData ?? '';
}

export type InvoiceStatus = 'paid' | 'cancelled' | 'failed' | 'pending' | 'unavailable';

/** Opens a Stars invoice via Telegram Mini App. Resolves with the final status. */
export function openInvoice(url: string): Promise<InvoiceStatus> {
  const tg = getTelegram();
  if (!tg || typeof tg.openInvoice !== 'function') return Promise.resolve('unavailable');
  return new Promise((resolve) => {
    tg.openInvoice(url, (status) => resolve(status));
  });
}

